/**
 * CSV Export Utility
 * 
 * Provides reusable functions for generating and downloading CSV files
 */

/**
 * Converts array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} headers - Array of header objects with { key, label } format
 * @returns {string} CSV formatted string
 */
export const arrayToCSV = (data, headers) => {
  if (!data || data.length === 0) return '';
  
  // Create header row
  const headerRow = headers.map(header => `"${header.label}"`).join(',');
  
  // Create data rows
  const dataRows = data.map(row => {
    return headers.map(header => {
      const value = row[header.key];
      // Handle different data types and escape quotes
      const stringValue = value !== null && value !== undefined ? String(value) : '';
      return `"${stringValue.replace(/"/g, '""')}"`;
    }).join(',');
  });
  
  return [headerRow, ...dataRows].join('\n');
};

/**
 * Downloads CSV data as a file
 * @param {string} csvData - CSV formatted string
 * @param {string} filename - Name for the downloaded file (without .csv extension)
 */
export const downloadCSV = (csvData, filename) => {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    URL.revokeObjectURL(url);
  }
};

/**
 * One-step function to export array of objects to CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects with { key, label } format
 * @param {string} filename - Name for the downloaded file (without .csv extension)
 */
export const exportToCSV = (data, headers, filename) => {
  const csvData = arrayToCSV(data, headers);
  downloadCSV(csvData, filename);
};

export default {
  arrayToCSV,
  downloadCSV,
  exportToCSV
};